import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const services = [
  {
    title: "Mergers & Acquisitions Advisory",
    icon: "Award",
    desc: "Strategic transaction advisory for acquisitions, restructuring, growth expansion, and post-merger integration.",
    items: ["Buy-Side & Sell-Side", "Valuation Analysis", "Strategic Partnerships"],
    isPremium: true,
    link: "#ma-division"
  },
  {
    title: "M&A General Inquiry",
    icon: "Target",
    desc: "Clarity on complex deal structures and strategic decision support for early-stage advisory.",
    items: ["Deal Flow Analysis", "Strategic Positioning", "Risk Evaluation"],
    link: "mailto:admin@whiteknightgc.com"
  },
  {
    title: "Compliance & Structuring",
    icon: "Shield",
    desc: "Navigating regulatory landscapes and identifying structural risks before they impact the deal.",
    items: ["Regulatory Audits", "Entity Optimization", "Integration Planning"],
    link: "mailto:admin@whiteknightgc.com"
  },
  {
    title: "Fundraising Strategy",
    icon: "TrendingUp",
    desc: "Positioning your business for maximum investor interest and optimal capital structure.",
    items: ["Investor Deck Review", "Valuation Modeling", "Capital Readiness"],
    link: "mailto:admin@whiteknightgc.com"
  },
  {
    title: "Transaction Support",
    icon: "Briefcase",
    desc: "End-to-end due diligence direction and process navigation with specialist coordination.",
    items: ["Due Diligence Oversight", "Negotiation Strategy", "Post-Merge Planning"],
    link: "mailto:admin@whiteknightgc.com"
  }
];

const Services = () => {
  return (
    <section id="services" className="py-24 md:py-32 bg-brand-white relative overflow-hidden">
      <div className="absolute inset-0 bg-pattern opacity-[0.03] pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-brand-black">Advisory Expertise</h2>
          <p className="text-lg text-brand-muted font-light">Direct strategic intervention across the entire M&A lifecycle.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {services.map((service, index) => (
            <motion.a 
              href={service.link}
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(182, 141, 64, 0.08)" }}
              className={`group p-10 bg-white border transition-all duration-500 flex flex-col relative overflow-hidden ${
                service.isPremium ? 'border-brand-gold bg-brand-gray/30 lg:col-span-1 md:col-span-2' : 'border-brand-border hover:border-brand-gold'
              }`}
            >
              {/* Animated Border Accent */}
              <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              
              {service.isPremium && (
                <div className="absolute top-4 right-4 text-[10px] font-black tracking-widest text-brand-gold uppercase bg-white px-2 py-1 border border-brand-gold/20 rounded-sm">
                  Premium
                </div>
              )}
              
              <div className="flex items-center gap-5 mb-8">
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center transition-all ${
                  service.isPremium ? 'bg-brand-gold text-white' : 'bg-brand-gold/10 text-brand-gold group-hover:bg-brand-gold group-hover:text-white'
                }`}>
                  <SafeIcon name={service.icon} className="text-2xl" />
                </div>
                <h3 className={`text-2xl font-bold tracking-tight ${service.isPremium ? 'text-brand-black' : 'text-brand-black'}`}>
                  {service.title}
                </h3>
              </div>

              <p className="text-brand-muted mb-8 leading-relaxed font-light">
                {service.desc}
              </p>

              <ul className="mt-auto space-y-3 mb-8">
                {service.items.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-bold text-brand-black uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 bg-brand-gold rounded-full"></span>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-2 text-brand-gold font-bold text-sm uppercase tracking-widest">
                <span>{service.isPremium ? 'Explore Division' : 'Inquire Now'}</span>
                <SafeIcon name="ArrowRight" className="group-hover:translate-x-2 transition-transform" />
              </div>
            </motion.a>
          ))}
        </div>

        <div className="mt-20 text-center">
          <a 
            href="mailto:admin@whiteknightgc.com" 
            className="inline-flex items-center gap-4 text-lg font-bold text-brand-black hover:text-brand-gold transition-all group"
          >
            <span className="w-12 h-[1px] bg-brand-gold group-hover:w-16 transition-all"></span>
            Email us to discuss your specific situation
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;